import { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signInWithPopup, signInWithEmailAndPassword, createUserWithEmailAndPassword, signOut, updateProfile } from 'firebase/auth';
import { doc, setDoc, getDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db, googleProvider } from '../lib/firebase';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        setUser(firebaseUser);
        const profileDoc = await getDoc(doc(db, 'users', firebaseUser.uid));
        if (profileDoc.exists()) setProfile(profileDoc.data());
      } else {
        setUser(null);
        setProfile(null);
      }
      setLoading(false);
    });
    return unsub;
  }, []);

  async function createUserProfile(firebaseUser, extra = {}) {
    const ref = doc(db, 'users', firebaseUser.uid);
    const existing = await getDoc(ref);
    if (!existing.exists()) {
      const data = {
        uid: firebaseUser.uid,
        displayName: firebaseUser.displayName || extra.displayName || 'Umoja User',
        email: firebaseUser.email,
        photoURL: firebaseUser.photoURL || null,
        country: extra.country || 'Uganda',
        bio: '', followers: 0, following: 0, posts: 0,
        createdAt: serverTimestamp(), ...extra
      };
      await setDoc(ref, data);
      setProfile(data);
      return data;
    }
    setProfile(existing.data());
    return existing.data();
  }

  async function loginWithGoogle() {
    const result = await signInWithPopup(auth, googleProvider);
    await createUserProfile(result.user);
    return result.user;
  }

  async function loginWithEmail(email, password) {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result.user;
  }

  async function registerWithEmail(email, password, displayName, country) {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    await updateProfile(result.user, { displayName });
    await createUserProfile(result.user, { displayName, country });
    return result.user;
  }

  async function logout() { await signOut(auth); }

  return (
    <AuthContext.Provider value={{ user, profile, loading, loginWithGoogle, loginWithEmail, registerWithEmail, logout }}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() { return useContext(AuthContext); }
